import {
  Box, Card, CardContent, Chip, Grid, LinearProgress, Stack, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PublicIcon from '@mui/icons-material/Public';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { regOps } from '@/api/endpoints';
import type { MedicalJurisdiction } from '@/types/medical';

/**
 * RegulationOps — jurisdictions list.
 *
 * One card per jurisdiction with a traffic-light readiness signal:
 *   - green   = live, all mandatory tests passing on the active release
 *   - amber   = beta, missing test coverage, or a pending change request
 *   - red     = pre-launch (no active release) or a high-severity unresolved change request
 *   - grey    = inactive
 *
 * The actual readiness counters come from `/launch-readiness/` lazily on the
 * detail page. This list page colours by `launch_status` only, then surfaces
 * detail readiness inside.
 */

type LaunchStatus = MedicalJurisdiction['launch_status'];

const STATUS_META: Record<LaunchStatus, { label: string; color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactNode }> = {
  planning:    { label: 'Planning',    color: 'default', icon: <HourglassTopIcon /> },
  drafting:    { label: 'Drafting',    color: 'default', icon: <HourglassTopIcon /> },
  testing:     { label: 'Testing',     color: 'warning', icon: <HourglassTopIcon /> },
  legal_hold:  { label: 'Legal hold',  color: 'error',   icon: <ErrorIcon /> },
  rollout:     { label: 'Rollout',     color: 'warning', icon: <HourglassTopIcon /> },
  live:        { label: 'Live',        color: 'success', icon: <CheckCircleIcon /> },
  paused:      { label: 'Paused',      color: 'error',   icon: <ErrorIcon /> },
  retired:     { label: 'Retired',     color: 'default', icon: <ErrorIcon /> },
};

export function RegOpsJurisdictionsPage() {
  const q = useApiQuery({
    queryKey: ['regops', 'jurisdictions'],
    queryFn:  regOps.jurisdictions.list as () => Promise<MedicalJurisdiction[]>,
  });

  return (
    <>
      <PageHeader
        title="Jurisdictions"
        subtitle="Per-country launch readiness. Each jurisdiction owns its own rule set, sources, releases."
      />

      <DataState query={q} empty={{ title: 'No jurisdictions configured yet' }}>
        {(items) => (
          <Grid container spacing={2}>
            {items
              .slice()
              .sort((a, b) => a.code.localeCompare(b.code))
              .map((j) => <JurisdictionCard key={j.id} jurisdiction={j} />)}
          </Grid>
        )}
      </DataState>
    </>
  );
}

function JurisdictionCard({ jurisdiction }: { jurisdiction: MedicalJurisdiction }) {
  const meta = STATUS_META[jurisdiction.launch_status] ?? STATUS_META.planning;
  // Approximate "readiness" using a simple per-card heuristic that the
  // backend computes more precisely on the detail endpoint. Here we just
  // colour the card and let the detail page show real numbers.
  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        component={RouterLink}
        to={`/regulation-ops/jurisdictions/${jurisdiction.code}`}
        sx={{ textDecoration: 'none', color: 'inherit',
              borderColor: meta.color === 'success' ? 'success.main' :
                           meta.color === 'warning' ? 'warning.main' :
                           meta.color === 'error' ? 'error.main' :
                           'divider',
              borderWidth: 2,
              '&:hover': { borderColor: 'primary.main' } }}
      >
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%',
              bgcolor: 'background.subtle',
              color: meta.color === 'default' ? 'text.disabled'
                   : `${meta.color}.main`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {meta.icon}
            </Box>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="h6" component="div" className="tabular">
                {jurisdiction.code}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {jurisdiction.display_name}
              </Typography>
            </Box>
            <Chip size="small" label={meta.label} color={meta.color}
                  variant={jurisdiction.is_active ? 'filled' : 'outlined'} />
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            <Capability label="Telemedicine" enabled={jurisdiction.allows_telemedicine} />
            <Capability label="Async consult" enabled={jurisdiction.allows_async_consultation} />
            <Capability label="Cross-border" enabled={jurisdiction.allows_cross_border_telemedicine} />
            <Capability label="e-Rx registry"
                        enabled={Boolean(jurisdiction.eprescription_registry_code)} />
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

function Capability({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{
        width: 8, height: 8, borderRadius: '50%',
        bgcolor: enabled ? 'success.main' : 'divider',
        flexShrink: 0,
      }} />
      <Typography variant="caption" color={enabled ? 'text.primary' : 'text.disabled'}>
        {label}
      </Typography>
    </Stack>
  );
}

// Suppress unused-imports lint when LinearProgress ends up unreferenced;
// kept for clarity and future readiness bar additions.
void LinearProgress; void PublicIcon;
